import * as sessionReplay from '@amplitude/session-replay-browser'
import type { Dict } from '@chakra-ui/utils'
import { AnalyticsBrowser } from '@segment/analytics-next'

// @ts-ignore
import c from 'js-cookie'
import mixpanel from 'mixpanel-browser'

// Import store to access user data
let store: typeof import('../store').store | null = null

try {
  // Dynamic import to avoid circular dependencies
  import('../store').then(storeModule => {
    store = storeModule.store
  })
} catch (_error) {
  // Silent fail for store import
}

interface ICookies {
  get: (key: string) => string | undefined
}

const Cookies: ICookies = c

export enum Destinations {
  Mixpanel,
  Segment
}

let segment: AnalyticsBrowser

// Helper function to get the correct domain for cross-subdomain cookies
const getDomainForCookies = (): string => {
  const hostname = window.location.hostname

  // For local development
  if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
    return hostname
  }

  // For subdomain sharing, use the root domain (e.g., .blueprint.ai)
  const domain = hostname.includes('.')
    ? `.${hostname.split('.').slice(-2).join('.')}`
    : hostname

  return domain
}

// Helper function to get session ID from cookie
const getStoredSessionId = (): number => {
  const sessionId = Cookies.get('analytics_session_id')
  return sessionId ? parseInt(sessionId, 10) : 0
}

// Helper function to get all session replay configuration
const getSessionReplayConfig = () => {
  return {
    apiKey: process.env.REACT_APP_AMPLITUDE_API_KEY,
    isConfigured: !!process.env.REACT_APP_AMPLITUDE_API_KEY,
    sampleRate: 0.1,
    serverZone:
      (process.env.REACT_APP_AMPLITUDE_SERVER_ZONE as 'US' | 'EU') || 'US'
  }
}

// Helper function to set cookies safely
const setCookie = (
  name: string,
  value: string,
  options: {
    domain?: string
    path?: string
    sameSite?: string
  } = {}
) => {
  const { domain, path = '/', sameSite = 'lax' } = options
  const cookieValue = `${name}=${value}; path=${path}; domain=${domain}; secure; samesite=${sameSite}`
  document.cookie = cookieValue
}

// Initialize Amplitude Session Replay
async function initAmplitudeSessionReplay() {
  const config = getSessionReplayConfig()
  if (!segment || !config.isConfigured || !config.apiKey) return

  try {
    const user = await segment.user()
    const storedSessionId = getStoredSessionId()
    const anonymousId = user.anonymousId()

    if (!anonymousId) return

    await sessionReplay.init(config.apiKey, {
      sessionId: storedSessionId,
      deviceId: anonymousId,
      sampleRate: config.sampleRate,
      serverZone: config.serverZone,
      debugMode: process.env.REACT_APP_ENV === 'development',
      optOut: false
    }).promise

    // Add middleware to handle session ID changes
    segment.addSourceMiddleware(({ payload, next }) => {
      const storedSessionId = getStoredSessionId()
      const integrations = payload.obj.integrations
      const amplitudeIntegration =
        integrations && typeof integrations === 'object'
          ? integrations['Actions Amplitude']
          : null
      const nextSessionId =
        amplitudeIntegration && typeof amplitudeIntegration === 'object'
          ? (amplitudeIntegration as { session_id?: number }).session_id || 0
          : 0

      if (
        typeof nextSessionId === 'number' &&
        storedSessionId < nextSessionId
      ) {
        const domain = getDomainForCookies()
        setCookie('analytics_session_id', String(nextSessionId), { domain })
        sessionReplay.setSessionId(nextSessionId)
      }
      next(payload)
    })

    // Add middleware to include session replay properties
    segment.addSourceMiddleware(({ payload, next }) => {
      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      if (payload.type() === 'track') {
        payload.obj.properties = {
          ...payload.obj.properties,
          ...sessionReplayProperties
        }
      }
      next(payload)
    })
  } catch (_error) {
    // Silent fail for session replay initialization
  }
}

export function initTracking() {
  mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN as string, {
    api_host: process.env.REACT_APP_MIXPANEL_API_HOST,
    autocapture: {
      pageview: 'full-url',
      click: true,
      input: true,
      scroll: false,
      submit: true,
      capture_text_content: true
    },
    cross_subdomain_cookie: true,
    persistence: 'cookie'
  })

  if (process.env.REACT_APP_SEGMENT_WRITE_KEY) {
    segment = AnalyticsBrowser.load({
      writeKey: process.env.REACT_APP_SEGMENT_WRITE_KEY as string
    })

    const config = getSessionReplayConfig()
    if (config.isConfigured) {
      initAmplitudeSessionReplay()
    }
  }
}

export function resetIdentity() {
  mixpanel.reset()
  if (segment) {
    segment.reset()
  }
}

/**
 * User Identification
 */
export async function identify(clinicianId: string, properties?: object) {
  // Get user properties including firstName and lastName
  const userProperties = getUserProperties()

  // Merge user properties with any passed-in properties
  const enhancedProperties = {
    ...userProperties,
    ...properties
  }

  // Mixpanel identification (immediate)
  mixpanel.identify(clinicianId)
  mixpanel.people.set(enhancedProperties)

  // Segment identification - wait for completion to ensure traits are set
  if (segment) {
    await segment.identify(clinicianId, enhancedProperties)
  }
}

export function getSegmentIdentity() {
  if (segment) {
    return segment.instance?.user().anonymousId()
  }
}

export async function identifyAnonymous(properties: object) {
  if (segment) {
    return segment.identify(properties)
  }
}

export async function group(
  organizationId: string,
  properties?: object
): Promise<void> {
  if (segment) {
    await segment.group(organizationId, properties)
  }
}

export function registerProperties(properties: Dict) {
  mixpanel.register(properties)
}

export function setUserProperties(properties: object) {
  mixpanel.people.set(properties)
}

export function trackEvent(
  event: string,
  payload?: object,
  destinations: Destinations[] = [Destinations.Mixpanel, Destinations.Segment]
) {
  const platform = window.location.pathname.includes('/extension')
    ? 'extension'
    : window.location.pathname.includes('widget')
      ? 'widget'
      : 'web'

  // Get synchronous properties
  const facebookProperties = getFacebookProperties()
  const userProperties = getUserProperties()

  // Get IP addresses asynchronously and send single event
  getIPAddresses()
    .then(ipAddresses => {
      const enhancedPayload = {
        ...payload,
        sessionPlatform: platform,
        ...facebookProperties,
        ...userProperties,
        ...ipAddresses
      }

      if (destinations.includes(Destinations.Mixpanel)) {
        mixpanel.track(event, enhancedPayload)
      }

      if (segment && destinations.includes(Destinations.Segment)) {
        // Include traits in context for Segment
        const segmentOptions = {
          context: {
            traits: userProperties
          }
        }
        segment.track(event, enhancedPayload, segmentOptions)
      }
    })
    .catch(error => {
      console.warn(
        'Could not fetch IP addresses for tracking, sending without IP data:',
        error
      )

      // Send event without IP addresses if IP fetch fails
      const enhancedPayload = {
        ...payload,
        sessionPlatform: platform,
        ...facebookProperties,
        ...userProperties,
        ipv4: null,
        ipv6: null
      }

      if (destinations.includes(Destinations.Mixpanel)) {
        mixpanel.track(event, enhancedPayload)
      }

      if (segment && destinations.includes(Destinations.Segment)) {
        // Include traits in context for Segment
        const segmentOptions = {
          context: {
            traits: userProperties
          }
        }
        segment.track(event, enhancedPayload, segmentOptions)
      }
    })
}

export async function trackEventAsync(
  event: string,
  payload?: object,
  destinations: Destinations[] = [Destinations.Mixpanel, Destinations.Segment]
) {
  const platform = window.location.pathname.includes('/extension')
    ? 'extension'
    : window.location.pathname.includes('widget')
      ? 'widget'
      : 'web'

  // Get enhanced properties
  const facebookProperties = getFacebookProperties()
  const userProperties = getUserProperties()
  const ipAddresses = await getIPAddresses()

  // Combine all properties
  const enhancedPayload = {
    ...payload,
    sessionPlatform: platform,
    ...facebookProperties,
    ...userProperties,
    ...ipAddresses
  }

  if (destinations.includes(Destinations.Mixpanel)) {
    mixpanel.track(event, enhancedPayload)
  }

  if (segment && destinations.includes(Destinations.Segment)) {
    // Include traits in context for Segment
    const segmentOptions = {
      context: {
        traits: userProperties
      }
    }
    segment.track(event, enhancedPayload, segmentOptions)
  }
}

export async function trackPageView() {
  if (segment) {
    const facebookProperties = getFacebookProperties()
    const userProperties = getUserProperties()
    const ipAddresses = await getIPAddresses()

    const enhancedProperties = {
      ...facebookProperties,
      ...userProperties,
      ...ipAddresses
    }

    // Include traits in context for Segment
    const segmentOptions = {
      context: {
        traits: userProperties
      }
    }
    segment.page(enhancedProperties, segmentOptions)
  }
}

function getFacebookProperties() {
  const fbp = Cookies.get('_fbp')
  const fbc = Cookies.get('_fbc')

  return { fbp, fbc }
}

function getUserProperties() {
  if (!store) {
    return {}
  }

  try {
    const authState = store.getState()?.auth
    const user = authState?.user

    if (!user) {
      return {}
    }

    return {
      userId: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email
    }
  } catch (error) {
    console.warn('Error getting user properties for tracking:', error)
    return {}
  }
}

// IP address cache - stores IP for 5 minutes
let ipCache: {
  data: { ipv4: string | null; ipv6: string | null } | null
  timestamp: number | null
} = {
  data: null,
  timestamp: null
}

const _IP_CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

async function getIPAddresses() {
  const now = Date.now()

  // Return cached result if still valid (5 minutes)
  if (ipCache?.timestamp && now - ipCache.timestamp < 5 * 60 * 1000) {
    return ipCache.data
  }

  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    const ip = data.ip

    if (!ip) {
      const result = { ipv4: null, ipv6: null }
      ipCache = { data: result, timestamp: now }
      return result
    }

    // Detect if IP is IPv4 or IPv6 based on format
    const isIPv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(ip)
    const isIPv6 = /^([0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}$/i.test(ip)

    const result = {
      ipv4: isIPv4 ? ip : null,
      ipv6: isIPv6 ? ip : null
    }

    // Cache the result
    ipCache = { data: result, timestamp: now }
    return result
  } catch (_error) {
    const result = { ipv4: null, ipv6: null }
    // Cache the error result too to avoid repeated failed calls
    ipCache = { data: result, timestamp: now }
    return result
  }
}

// Cross-app session continuity helper
export const getCrossAppSessionInfo = () => {
  const storedSessionId = getStoredSessionId()
  const anonymousId = segment?.instance?.user().anonymousId()

  return {
    sessionId: storedSessionId,
    anonymousId,
    domain: getDomainForCookies(),
    hasCrossAppSession: !!(storedSessionId && anonymousId)
  }
}

// Session Replay utility functions
export const getSessionReplayInfo = () => {
  const anonymousId = segment?.instance?.user().anonymousId()
  const userId = segment?.instance?.user().id()

  let sessionReplayProperties = null
  let isRecording = false

  try {
    sessionReplayProperties = sessionReplay.getSessionReplayProperties()
    isRecording = !!sessionReplayProperties
  } catch (_error) {
    // Session replay not initialized or not available
  }

  const crossAppInfo = getCrossAppSessionInfo()
  const config = getSessionReplayConfig()

  return {
    isActive: config.isConfigured,
    isRecording,
    sessionId:
      sessionReplayProperties?.['[Amplitude] Session Replay ID'] || null,
    anonymousId,
    userId,
    hasAmplitudeKey: config.isConfigured,
    sampleRate: config.sampleRate,
    environment: process.env.REACT_APP_ENV,
    storedSessionId: getStoredSessionId(),
    crossAppContinuity: crossAppInfo
  }
}

if (typeof window !== 'undefined') {
  ;(window as any).getSessionReplayInfo = getSessionReplayInfo
  ;(window as any).getCrossAppSessionInfo = getCrossAppSessionInfo
}
